/**
 * Phase 6 admin UI — surfaces backend/tag_review.py's queue (cards mobile/web
 * flagged needsAdminReview) so an admin can actually see and act on it.
 * Previously this data had nowhere to render even though the backend
 * (/api/tag-review, PATCH /api/tag-review/{id}) was fully built.
 */
import React, { useEffect, useState, useCallback } from "react";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import { SkeletonTable } from "../components/ui/Skeleton";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, RefreshCw, Inbox } from "lucide-react";
import { apiListTagReviewQueue, apiResolveTagReviewItem } from "../lib/cloudSync";

const STATUS_TABS = ["pending", "resolved", "dismissed"];

const TagReviewQueue = () => {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (s) => {
    setLoading(true);
    setError("");
    try {
      const data = await apiListTagReviewQueue(s);
      setItems(data?.items || []);
    } catch (e) {
      setError(e?.message || "Failed to load review queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(status); }, [status, load]);

  const resolve = async (id, newStatus) => {
    setBusyId(id);
    try {
      await apiResolveTagReviewItem(id, newStatus);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (e) {
      setError(e?.message || "Failed to update item");
    } finally {
      setBusyId(null);
    }
  };

  return (
      <div className="max-w-5xl space-y-6">
          <PageHeader
            title="AI Tag Review Queue"
            subtitle="Cards mobile/web flagged for low-confidence or AI-only tag resolution. Promote a clear pattern into a permanent tag rule, or dismiss it."
            actions={
              <button
                onClick={() => load(status)}
                className="text-xs text-celestial-muted hover:text-celestial-text flex items-center gap-1.5 shrink-0"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            }
          />

          <div className="flex items-center gap-1">
            {STATUS_TABS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                  status === s
                    ? "bg-glow-mid/15 text-celestial-text"
                    : "text-celestial-muted hover:text-celestial-text"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {error && (
            <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{error}</div>
          )}

          {loading ? (
            <SkeletonTable rows={4} />
          ) : (
          <div className="card-celestial rounded-2xl divide-y divide-celestial-border/10">
            {items.length === 0 ? (
              <EmptyState icon={Inbox} title={`Nothing ${status} right now.`} description="New flags from mobile/web will show up here automatically." />
            ) : (
              items.map((item) => (
                <div key={item.id} className="p-4 flex items-start justify-between gap-4">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-celestial-text">{item.cardName}</span>
                      {item.seen_count > 1 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface/60 text-celestial-muted">
                          seen ×{item.seen_count}
                        </span>
                      )}
                      {typeof item.confidence === "number" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface/60 text-celestial-muted">
                          confidence {Math.round(item.confidence * 100)}%
                        </span>
                      )}
                    </div>
                    {item.deckName && (
                      <div className="text-xs text-celestial-muted">From deck: {item.deckName}</div>
                    )}
                    {item.suggestedTags?.length > 0 && (
                      <div className="text-xs text-celestial-muted">
                        Suggested: {item.suggestedTags.join(", ")}
                      </div>
                    )}
                    {item.reason && (
                      <div className="text-xs text-celestial-muted/80 italic">{item.reason}</div>
                    )}
                    <div className="text-[11px] text-celestial-muted/60">
                      Last seen {item.last_seen_at ? new Date(item.last_seen_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  {status === "pending" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        disabled={busyId === item.id}
                        onClick={() => resolve(item.id, "resolved")}
                        className="btn-celestial rounded-lg"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === item.id}
                        onClick={() => resolve(item.id, "dismissed")}
                        className="rounded-lg border-celestial-border/30 text-celestial-muted"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Dismiss
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          )}
      </div>
  );
};

export default TagReviewQueue;
